import { useState, useEffect, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Card, CardBody, CardHeader, Button, Spinner, Alert, Badge, Switch } from '@components/ui';
import {
  CheckCircleIcon,
  SparklesIcon,
  ArrowUpIcon,
  XMarkIcon,
  GlobeAltIcon,
} from '@heroicons/react/24/outline';
import { cn } from '@lib/utils/cn';
import {
  useUpgradePlan,
  useDowngradePlan,
  useChangePlanWithPayment,
  useCurrentSubscription,
  useCreateFreeSubscription
} from '../hooks/useSubscriptions';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { subscriptionService } from '../services/subscriptionService';
import { certificateBundleService } from '../services/certificateBundleService';
import { isBundleEligiblePlan, isPromoPlan } from '../config/bundleConfig';
import { useBundlePrice } from '../hooks/useBundlePrice';
import type { AvailablePlan, CardInfo, SubscriptionPlan } from '../types/subscription.types';
import { PaymentModal } from './PaymentModal';
import { useAuth } from '@features/auth/hooks/useAuth';
import {
  showSuccessAlert,
  showErrorAlert,
  showWarningAlert,
  showConfirmDialog,
} from '@lib/utils/swal';
import { trackingHelpers } from '@/services/mixpanel';

export function PlansPage() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const location = useLocation();
  const navigate = useNavigate();

  // Primero verificar si hay suscripción activa
  const { data: currentSubscription, isLoading: isLoadingCurrentSub } = useCurrentSubscription();
  const hasActiveSubscription = currentSubscription?.status === 'active' || currentSubscription?.status === 'trialing';

  // Si tiene suscripción activa, usar available-plans
  const { data: availablePlansData, isLoading: isLoadingAvailablePlans, error: availablePlansError } = useQuery({
    queryKey: ['subscription', 'available-plans'],
    queryFn: () => subscriptionService.getAvailablePlans(),
    enabled: hasActiveSubscription && !isLoadingCurrentSub,
    staleTime: 60 * 1000, // 1 minute
  });

  // Si NO tiene suscripción activa, obtener planes desde product info
  const { data: productData, isLoading: isLoadingProduct, error: productError } = useQuery({
    queryKey: ['subscription', 'product-info'],
    queryFn: () => subscriptionService.getProductInfo(),
    enabled: !hasActiveSubscription && !isLoadingCurrentSub,
  });

  const upgradeMutation = useUpgradePlan();
  const downgradeMutation = useDowngradePlan();
  const changePlanWithPaymentMutation = useChangePlanWithPayment();
  const createFreeSubscriptionMutation = useCreateFreeSubscription();
  // Precio del bundle: sale del catálogo, no de una env var.
  const { price: bundlePrice } = useBundlePrice();
  const bundlePriceLabel = bundlePrice != null ? `$${bundlePrice.toFixed(2)}` : '—';

  const isLoadingPlans = isLoadingCurrentSub || (hasActiveSubscription ? isLoadingAvailablePlans : isLoadingProduct);
  const plansError = hasActiveSubscription ? availablePlansError : productError;

  // Determinar si el usuario puede gestionar la suscripción (comprar/cambiar plan)
  // Solo el usuario que activó la suscripción puede hacerlo
  const canManage = availablePlansData?.can_manage ?? currentSubscription?.canManage ?? !hasActiveSubscription;

  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<AvailablePlan | null>(null);
  const [isSupportModalOpen, setIsSupportModalOpen] = useState(false);
  const [isCreatingAndUpgrading, setIsCreatingAndUpgrading] = useState(false);
  // Para usuarios que acaban de crear suscripción, guardamos los datos temporalmente
  const [newUserSubscriptionInfo, setNewUserSubscriptionInfo] = useState<any>(null);
  const [bundlePlanId, setBundlePlanId] = useState<string | null>(null);
  const [bundleContext, setBundleContext] = useState<{ planId: string; registroId: string } | null>(null);
  const bundleNavigationHandledRef = useRef(false);
  const compraDirectaHandledRef = useRef(false);
  const bundleContextRef = useRef<{ planId: string; registroId: string } | null>(null);

  /**
   * Lo que cuesta el bundle de este plan: certificado + plan.
   *
   * Única fuente del número en toda la pantalla —precio de la tarjeta, botón,
   * resumen y modal de pago— para que no puedan discrepar entre sí. Tiene que
   * coincidir además con lo que cobra el servidor, que lo recalcula desde el
   * catálogo y nunca acepta el monto del cliente.
   */
  const precioBundle = (plan: AvailablePlan): number | null =>
    bundlePrice != null ? Math.round((bundlePrice + plan.price) * 100) / 100 : null;

  /**
   * Monto a cobrar cuando el usuario llega al pago.
   *
   * Solo es el del bundle si el certificado ya quedó registrado
   * (`bundleContext`); con el toggle recién encendido todavía falta pasar por
   * ese formulario, y ahí el flujo no cobra nada.
   */
  const getBundlePaymentAmount = (plan: AvailablePlan) =>
    bundleContextRef.current?.planId === plan.id
      ? precioBundle(plan) ?? plan.price_difference
      : plan.price_difference;

  useEffect(() => {
    const raw = sessionStorage.getItem('certificate_bundle_request');
    if (!raw) return;
    try {
      const parsed = JSON.parse(raw);
      if (parsed?.planId && parsed?.registroId) {
        const ctx = { planId: parsed.planId, registroId: parsed.registroId };
        bundleContextRef.current = ctx;
        setBundleContext(ctx);
        setBundlePlanId(parsed.planId);
      }
    } catch {
      // ignore invalid storage
    }
  }, []);

  // Determinar qué datos mostrar según si hay suscripción activa o no usando useMemo
  const { product, currentSubscriptionInfo, plans } = useMemo(() => {
    let product;
    let currentSubscriptionInfo: any = null;
    let plans: AvailablePlan[] = [];

    if (hasActiveSubscription && availablePlansData) {
      // Usuario con suscripción activa - usar available-plans
      product = availablePlansData.product;
      currentSubscriptionInfo = availablePlansData.current_subscription;
      plans = availablePlansData.available_plans || [];
    } else if (productData) {
      // Usuario sin suscripción - usar product info
      product = productData.product;
      // Mapear los planes del producto a formato AvailablePlan
      plans = productData.product.subscription_plans.map((plan: SubscriptionPlan) => ({
        id: plan.id,
        name: plan.name,
        description: plan.description,
        price: parseFloat(plan.price),
        currency: plan.currency,
        subscription_type: plan.subscription_type,
        monthly_price: parseFloat(plan.monthly_price),
        trial_days: parseInt(plan.trial_days),
        features: plan.features,
        limits: plan.limits,
        is_popular: plan.is_popular,
        is_current: false,
        change_type: 'upgrade' as const,
        price_difference: parseFloat(plan.price),
      }));
    }

    return { product, currentSubscriptionInfo, plans };
  }, [hasActiveSubscription, availablePlansData, productData]);

  // Ordenar planes por precio
  const sortedPlans = useMemo(() => {
    return [...plans].sort((a, b) => a.price - b.price);
  }, [plans]);

  // Track plans viewed when page loads (MUST be before any conditional returns)
  useEffect(() => {
    if (!isLoadingPlans && sortedPlans.length > 0) {
      console.log('[PlansPage] Tracking plans viewed:', {
        hasActiveSubscription,
        currentPlan: currentSubscriptionInfo?.plan_name,
        availablePlansCount: sortedPlans.length,
      });
      trackingHelpers.trackPlansViewed({
        hasActiveSubscription,
        currentPlan: currentSubscriptionInfo?.plan_name,
        availablePlansCount: sortedPlans.length,
      });
    }
  }, [isLoadingPlans, sortedPlans.length, hasActiveSubscription, currentSubscriptionInfo?.plan_name]);

  // Si regresamos del formulario del bundle, continuar con el flujo normal de pago del plan
  useEffect(() => {
    const state = location.state as { bundlePlanId?: string; bundleRegistroId?: string } | null;
    if (bundleNavigationHandledRef.current || !state?.bundlePlanId) {
      return;
    }

    if (isLoadingPlans || sortedPlans.length === 0) {
      return;
    }

    const selected = sortedPlans.find(plan => plan.id === state.bundlePlanId);
    if (!selected) {
      navigate(location.pathname, { replace: true, state: null });
      bundleNavigationHandledRef.current = true;
      return;
    }

    bundleNavigationHandledRef.current = true;
    setBundlePlanId(state.bundlePlanId);
    if (state.bundleRegistroId) {
      const ctx = { planId: state.bundlePlanId, registroId: state.bundleRegistroId };
      bundleContextRef.current = ctx;
      setBundleContext(ctx);
    }

    if (!hasActiveSubscription) {
      handlePremiumPlanForNewUser(selected);
    } else {
      handleChangePlan(selected);
    }

    navigate(location.pathname, { replace: true, state: null });
  }, [location.state, isLoadingPlans, sortedPlans, hasActiveSubscription, navigate, location.pathname]);

  /**
   * Compra directa desde fuera: el banner de campaña manda `comprarPlanId` y
   * acá se abre el pago de ese plan sin que el usuario tenga que buscarlo.
   *
   * Se espera a que los planes carguen —los enlaces llegan con la página en
   * blanco— y se limpia el state al terminar, para que recargar no vuelva a
   * abrir el modal. El ref evita dispararlo dos veces mientras React resuelve.
   *
   * Si el plan no se puede comprar (ya es el actual, es una bajada de nivel, o
   * el usuario no administra la suscripción) se aterriza en la página sin abrir
   * nada: es preferible a lanzar un flujo que el botón tendría deshabilitado.
   */
  useEffect(() => {
    const state = location.state as { comprarPlanId?: string } | null;
    if (compraDirectaHandledRef.current || !state?.comprarPlanId) {
      return;
    }

    if (isLoadingPlans || sortedPlans.length === 0) {
      return;
    }

    compraDirectaHandledRef.current = true;

    const seleccionado = sortedPlans.find(plan => plan.id === state.comprarPlanId);
    if (seleccionado && sePuedeComprar(seleccionado)) {
      iniciarCompraDePlan(seleccionado);
    }

    navigate(location.pathname, { replace: true, state: null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state, isLoadingPlans, sortedPlans, hasActiveSubscription, navigate, location.pathname]);

  /**
   * Punto único de entrada a la compra de un plan.
   *
   * Lo usan el botón de cada tarjeta y los enlaces directos (el banner de
   * campaña, el flujo del bundle). Al estar en un solo sitio, un enlace directo
   * no puede quedar desalineado con lo que hace el botón: el bundle redirige a
   * su propia pantalla en vez de abrir el modal, y quien no tiene suscripción
   * pasa por la creación de la gratuita antes del pago.
   */
  const iniciarCompraDePlan = async (plan: AvailablePlan) => {
    if (
      isBundleEligiblePlan(plan) &&
      bundlePlanId === plan.id &&
      (!bundleContext || bundleContext.planId !== plan.id)
    ) {
      navigate('/subscriptions/certificate-bundle', {
        state: { planId: plan.id, planName: plan.name, planPrice: plan.price },
      });
      return;
    }

    if (!hasActiveSubscription) {
      await handlePremiumPlanForNewUser(plan);
    } else {
      await handleChangePlan(plan);
    }
  };

  /**
   * ¿Se puede comprar este plan ahora mismo?
   *
   * Réplica de la condición que deshabilita el botón de la tarjeta. Un enlace
   * directo tiene que respetarla: si el plan es el actual, o es una bajada de
   * nivel, o el usuario no administra la suscripción, se aterriza en la página
   * sin abrir nada, en vez de lanzar un flujo que la interfaz no permitiría.
   */
  const sePuedeComprar = (plan: AvailablePlan): boolean => {
    if (!canManage) return false;
    if (!hasActiveSubscription) return true;
    return !plan.is_current && plan.change_type === 'upgrade';
  };

  const handleChangePlan = async (plan: AvailablePlan) => {
    // Solo permitir upgrades, no downgrades
    if (plan.change_type !== 'upgrade') {
      return;
    }

    // Track plan selection
    trackingHelpers.trackPlanSelected({
      planId: plan.id,
      planName: plan.name,
      planPrice: plan.price,
      planInterval: plan.subscription_type,
      currentPlan: currentSubscriptionInfo?.plan_name,
      actionType: plan.change_type,
    });

    // Track upgrade started
    if (currentSubscriptionInfo?.plan_name) {
      trackingHelpers.trackPlanUpgradeStarted({
        fromPlan: currentSubscriptionInfo.plan_name,
        toPlan: plan.name,
        fromPrice: currentSubscriptionInfo.price || 0,
        toPrice: plan.price,
      });
    }

    // Si el plan es un upgrade y tiene diferencia de precio, mostrar modal de pago
    const paymentAmount = getBundlePaymentAmount(plan);
    if (paymentAmount > 0) {
      setSelectedPlan({
        ...plan,
        price_difference: paymentAmount,
      });
      setIsPaymentModalOpen(true);
    } else {
      // Para upgrades sin costo adicional (no debería ocurrir normalmente)
      const result = await showConfirmDialog(
        '¿Cambiar de plan?',
        `¿Estás seguro de que deseas cambiar al ${plan.name}?`,
        {
          confirmButtonText: 'Sí, cambiar',
          cancelButtonText: 'Cancelar',
        }
      );

      if (result.isConfirmed) {
        upgradeMutation.mutate(
          { newPlanId: plan.id, priceId: plan.id },
          {
            onSuccess: async (response) => {
              // Track plan changed
              trackingHelpers.trackPlanChanged({
                planId: plan.id,
                planName: plan.name,
                planPrice: plan.price,
                previousPlan: currentSubscriptionInfo?.plan_name,
                changeType: 'upgrade',
              });

              await showSuccessAlert('¡Éxito!', response.message);
              // Recargar la página
              window.location.reload();
            },
            onError: async () => {
              await showErrorAlert(
                'Error',
                'Error al cambiar el plan. Por favor, intenta de nuevo.'
              );
            },
          }
        );
      }
    }
  };

  const handlePaymentSubmit = async (cardInfo: CardInfo) => {
    // Usar currentSubscriptionInfo o newUserSubscriptionInfo como fallback
    const subscriptionInfo = currentSubscriptionInfo || newUserSubscriptionInfo;

    if (!selectedPlan || !subscriptionInfo?.id) {
      await showErrorAlert(
        'Error',
        'No se pudo obtener la información del plan o suscripción'
      );
      return;
    }

    const activeBundle = bundleContextRef.current;
    const bundleRegistroId =
      activeBundle?.registroId && activeBundle.planId === selectedPlan.id
        ? activeBundle.registroId
        : undefined;

    const customerName =
      subscriptionInfo.customer_email?.split('@')[0] || user?.email?.split('@')[0] || 'Cliente';

    try {
      // El bundle tiene su propio camino: se cobra el CERTIFICADO y el plan se
      // cobra dentro del mismo cargo. El monto lo fija el servidor, no se envía.
      if (bundleRegistroId) {
        const result = await certificateBundleService.purchase({
          bundleRegistroId,
          planSubscriptionId: subscriptionInfo.id,
          planId: selectedPlan.id,
          cardInfo,
          customerName,
        });

        // El cobro salió. Si algo quedó a medias, el backend ya lo marcó como
        // crítico; acá se avisa para que el usuario no se quede con una idea
        // equivocada de lo que tiene.
        if (!result.certificate_registered) {
          await showErrorAlert(
            'Tu pago se procesó correctamente',
            'No pudimos confirmar el registro de tu certificado. Nuestro equipo ya fue notificado y lo completará. No se te volverá a cobrar.'
          );
        } else if (!result.plan_activated) {
          await showErrorAlert(
            'Tu certificado quedó pagado',
            'La activación de tu plan quedó pendiente y se aplicará en breve. Nuestro equipo ya fue notificado. No se te volverá a cobrar.'
          );
        }

        trackingHelpers.trackPlanChanged({
          planId: selectedPlan.id,
          planName: selectedPlan.name,
          planPrice: result.amount,
          previousPlan: subscriptionInfo?.plan_name || 'Sin Plan',
          changeType: 'upgrade',
        });

        setIsPaymentModalOpen(false);
        setSelectedPlan(null);
        setNewUserSubscriptionInfo(null);
        sessionStorage.removeItem('certificate_bundle_request');
        setBundleContext(null);
        bundleContextRef.current = null;

        await showSuccessAlert(
          '¡Listo!',
          'Tu certificado quedó pagado y tu plan activado. Te contactaremos para continuar con la emisión del certificado.'
        );

        window.location.href = '/subscriptions/plans';
        return;
      }

      const paymentResponse = await changePlanWithPaymentMutation.mutateAsync({
        subscriptionId: subscriptionInfo.id,
        newPlanId: selectedPlan.id,
        cardInfo,
        amount: selectedPlan.price_difference,
        customerName,
      });

      // Track plan changed after successful payment
      trackingHelpers.trackPlanChanged({
        planId: selectedPlan.id,
        planName: selectedPlan.name,
        planPrice: selectedPlan.price,
        previousPlan: subscriptionInfo?.plan_name || 'Sin Plan',
        changeType: 'upgrade',
      });

      // Close modal immediately (state cleanup deferred until after polling)
      setIsPaymentModalOpen(false);

      // Invalidate React Query cache
      await queryClient.invalidateQueries({ queryKey: ['subscription-verification'] });
      await queryClient.invalidateQueries({ queryKey: ['subscription', 'current'] });
      await queryClient.invalidateQueries({ queryKey: ['subscription', 'available-plans'] });
      await queryClient.invalidateQueries({ queryKey: ['subscription', 'product-info'] });
      await queryClient.invalidateQueries({ queryKey: ['consumption'] });

      // Phase 3: Handle plan_change_failed — payment went through but plan change didn't
      if (paymentResponse?.payment_completed === true && paymentResponse?.plan_change_failed === true) {
        console.log('⚠️ Pago exitoso pero cambio de plan falló, mostrando warning');
        await showWarningAlert(
          'Pago Procesado',
          'Tu pago fue procesado exitosamente. El cambio de plan se está aplicando y puede tardar unos minutos. No intentes pagar de nuevo. Si no ves el cambio en 10 minutos, contacta soporte.',
        );
        setSelectedPlan(null);
        setNewUserSubscriptionInfo(null);
        window.location.href = '/subscriptions/plans';
        return;
      }

      // Check if backend already verified the activation
      const backendVerified = paymentResponse?.verified === true &&
                              paymentResponse?.subscription_status === 'active';

      if (backendVerified) {
        // Backend confirmed activation - no need for polling
        console.log('✅ Backend confirmó activación, sin necesidad de polling');
        await showSuccessAlert(
          '¡Éxito!',
          'Plan actualizado y pago procesado exitosamente',
          { timer: 2000, showConfirmButton: true }
        );
      } else {
        // Phase 4: Progressive backoff polling — 5 attempts [2s, 3s, 5s, 8s, 12s]
        console.log('🔍 Backend no confirmó activación, verificando con polling progresivo...');
        const pollingDelays = [2000, 3000, 5000, 8000, 12000];
        const maxAttempts = pollingDelays.length;
        let isVerified = false;
        const expectedPlanId = selectedPlan.id;
        const expectedPlanName = selectedPlan.name;

        for (let attempt = 1; attempt <= maxAttempts && !isVerified; attempt++) {
          await new Promise(resolve => setTimeout(resolve, pollingDelays[attempt - 1]));
          try {
            console.log(`🔄 Intento de verificación ${attempt}/${maxAttempts} (delay: ${pollingDelays[attempt - 1]}ms)...`);
            await queryClient.invalidateQueries({ queryKey: ['subscription', 'available-plans'] });
            const updatedSubscription = await subscriptionService.getAvailablePlans();
            const currentPlan = updatedSubscription.current_subscription;

            const planMatches = currentPlan?.plan_id === expectedPlanId ||
                               currentPlan?.plan_name === expectedPlanName;
            const isActive = currentPlan?.status === 'active' || currentPlan?.status === 'trialing';

            if (isActive && planMatches) {
              isVerified = true;
              console.log('✅ Plan verificado correctamente');
            }
          } catch (verifyError) {
            console.error('⚠️ Error al verificar:', verifyError);
          }
        }

        if (isVerified) {
          await showSuccessAlert(
            '¡Éxito!',
            'Plan actualizado y pago procesado exitosamente',
            { timer: 2000, showConfirmButton: true }
          );
        } else {
          await showWarningAlert(
            'Pago Procesado',
            'Tu pago fue procesado exitosamente. El plan puede tardar unos minutos en activarse. Si no ves el cambio, por favor recarga la página.',
          );
        }
      }

      // Clean up state before redirect
      setSelectedPlan(null);
      setNewUserSubscriptionInfo(null);
      if (bundleContextRef.current?.planId === selectedPlan?.id) {
        sessionStorage.removeItem('certificate_bundle_request');
        setBundleContext(null);
        bundleContextRef.current = null;
      }

      // Reload page to show the new plan
      window.location.href = '/subscriptions/plans';
    } catch (error: any) {
      throw error;
    }
  };

  const handleCreateFreeSubscription = async () => {
    // Validar que NO sea el email de hypernovalabs
    if (user?.email === 'info@hypernovalabs.com') {
      await showWarningAlert(
        'No permitido',
        'No puedes crear una suscripción gratuita con esta cuenta.'
      );
      return;
    }

    const result = await showConfirmDialog(
      '¿Activar plan gratuito?',
      '¿Deseas activar el plan gratuito para esta compañía?',
      {
        confirmButtonText: 'Sí, activar',
        cancelButtonText: 'Cancelar',
      }
    );

    if (result.isConfirmed) {
      createFreeSubscriptionMutation.mutate(undefined, {
        onSuccess: async (response: any) => {
          await showSuccessAlert(
            '¡Éxito!',
            response.message || 'Suscripción gratuita creada exitosamente',
            { timer: 2000, showConfirmButton: true }
          );
          // Recargar la página para mostrar el nuevo plan
          window.location.reload();
        },
        onError: async (error: any) => {
          let errorTitle = 'Error';
          let errorMessage = 'Error al crear la suscripción gratuita. Por favor, intenta de nuevo.';

          const backendMessage = error?.response?.data?.message ||
                                error?.response?.data?.detail ||
                                error?.message;

          if (backendMessage) {
            if (backendMessage.includes('already exists') || backendMessage.includes('Subscription already exists')) {
              errorTitle = 'Suscripción Existente';
              errorMessage = 'Ya existe una suscripción activa para esta compañía. Por favor, recarga la página.';
            } else if (backendMessage.includes('NumberId') || backendMessage.includes('RUC')) {
              errorTitle = 'Datos Incompletos';
              errorMessage = 'La compañía no tiene RUC configurado. Por favor, completa los datos de la empresa.';
            } else {
              errorMessage = backendMessage;
            }
          }

          await showErrorAlert(errorTitle, errorMessage);
        },
      });
    }
  };

  // Nuevo: Manejar compra de plan premium para usuarios sin suscripción
  const handlePremiumPlanForNewUser = async (plan: AvailablePlan) => {
    // Validar que NO sea el email de hypernovalabs
    if (user?.email === 'info@hypernovalabs.com') {
      await showWarningAlert(
        'No permitido',
        'No puedes activar planes con esta cuenta.'
      );
      return;
    }

    // Si es plan gratuito, usar el flujo normal
    if (plan.price === 0) {
      handleCreateFreeSubscription();
      return;
    }

    // Confirmar con el usuario
    const bundleAmount = getBundlePaymentAmount(plan);
    const result = await showConfirmDialog(
      `¿Activar ${plan.name}?`,
      `Se activará primero una suscripción base y luego se procesará el pago de $${bundleAmount.toFixed(2)} para el ${plan.name}.`,
      {
        confirmButtonText: 'Continuar',
        cancelButtonText: 'Cancelar',
      }
    );

    if (!result.isConfirmed) {
      return;
    }

    setIsCreatingAndUpgrading(true);

    try {
      // Paso 0: Verificar si ya existe una suscripción (por si los datos están desactualizados)
      console.log('🔍 Paso 0: Verificando si ya existe suscripción...');
      try {
        const existingPlans = await subscriptionService.getAvailablePlans();
        if (existingPlans.current_subscription?.id) {
          console.log('✅ Ya existe suscripción, saltando creación:', existingPlans.current_subscription);
          // Ya tiene suscripción, ir directo al pago
          const updatedPlan = existingPlans.available_plans?.find(
            (p: AvailablePlan) => p.id === plan.id
          );

          if (updatedPlan) {
            setNewUserSubscriptionInfo(existingPlans.current_subscription);
            setSelectedPlan({
              ...updatedPlan,
              price_difference: getBundlePaymentAmount(updatedPlan) ?? updatedPlan.price_difference ?? plan.price,
            });
            setIsPaymentModalOpen(true);
            setIsCreatingAndUpgrading(false);
            return;
          }
        }
      } catch (checkError) {
        console.log('⚠️ No se pudo verificar suscripción existente, continuando con creación...', checkError);
      }

      // Paso 1: Crear suscripción gratuita y capturar el ID del response
      console.log('📝 Paso 1: Creando suscripción base...');
      const createResponse = await createFreeSubscriptionMutation.mutateAsync(undefined);
      const directSubscriptionId = (createResponse as any)?.subscription?.id;
      console.log('✅ Suscripción base creada, directSubscriptionId:', directSubscriptionId);

      // Paso 2: Polling progresivo para obtener la suscripción vía available-plans
      console.log('🔄 Paso 2: Polling progresivo para obtener datos de suscripción...');
      await queryClient.invalidateQueries({ queryKey: ['subscription', 'current'] });
      await queryClient.invalidateQueries({ queryKey: ['subscription', 'available-plans'] });

      const pollingDelays = [1000, 2000, 3000, 4000, 5000];
      let availablePlans: any = null;
      let updatedPlan: AvailablePlan | undefined;
      let subscriptionId: string | undefined;

      for (let attempt = 0; attempt < pollingDelays.length; attempt++) {
        await new Promise(resolve => setTimeout(resolve, pollingDelays[attempt]));
        try {
          console.log(`🔄 Polling intento ${attempt + 1}/${pollingDelays.length} (delay: ${pollingDelays[attempt]}ms)...`);
          await queryClient.invalidateQueries({ queryKey: ['subscription', 'available-plans'] });
          availablePlans = await subscriptionService.getAvailablePlans();
          subscriptionId = availablePlans?.current_subscription?.id;
          updatedPlan = availablePlans?.available_plans?.find(
            (p: AvailablePlan) => p.id === plan.id
          );

          if (subscriptionId && updatedPlan) {
            console.log('✅ Datos encontrados via polling:', { subscriptionId, updatedPlan: updatedPlan.name });
            break;
          }
        } catch (pollError) {
          console.warn(`⚠️ Polling intento ${attempt + 1} falló:`, pollError);
        }
      }

      // Fallback: si polling no encontró datos pero tenemos el ID directo del paso 1
      if (!subscriptionId && directSubscriptionId) {
        console.log('⚠️ Polling no encontró suscripción, usando directSubscriptionId como fallback:', directSubscriptionId);
        subscriptionId = directSubscriptionId;
      }

      if (!subscriptionId) {
        throw new Error('No se pudo obtener el ID de la suscripción');
      }

      // Si no encontramos el plan actualizado via polling, usar los datos originales
      if (!updatedPlan) {
        console.log('⚠️ Plan no encontrado en available_plans, usando datos originales');
        updatedPlan = { ...plan };
      }

      console.log('✅ Plan encontrado con price_difference:', updatedPlan.price_difference);

      // Guardar la info de suscripción para el flujo de pago
      const subscriptionInfo = availablePlans?.current_subscription || { id: subscriptionId };
      setNewUserSubscriptionInfo(subscriptionInfo);

      // Paso 3: Abrir modal de pago con los datos actualizados
      setSelectedPlan({
        ...updatedPlan,
        price_difference: getBundlePaymentAmount(updatedPlan) ?? updatedPlan.price_difference ?? plan.price,
      });
      setIsPaymentModalOpen(true);

    } catch (error: any) {
      // Extraer mensaje de error más específico para mostrar al usuario
      let errorMessage = 'Error al preparar la suscripción. Por favor, intenta de nuevo.';
      let errorTitle = 'Error';

      // Priorizar mensajes del backend
      const backendMessage = error?.response?.data?.message ||
                            error?.response?.data?.detail ||
                            error?.response?.data?.error ||
                            error?.message;

      if (backendMessage) {
        // Traducir mensajes comunes al español
        if (backendMessage.includes('already exists') || backendMessage.includes('Subscription already exists')) {
          errorTitle = 'Suscripción Existente';
          errorMessage = 'Ya existe una suscripción activa para esta compañía. Por favor, recarga la página para ver tu plan actual.';
        } else if (backendMessage.includes('NumberId not found') || backendMessage.includes('RUC')) {
          errorTitle = 'Datos Incompletos';
          errorMessage = 'La compañía no tiene RUC configurado. Por favor, completa los datos de la empresa primero.';
        } else if (backendMessage.includes('plan not found') || backendMessage.includes('Plan not found')) {
          errorTitle = 'Plan No Disponible';
          errorMessage = 'El plan seleccionado no está disponible. Por favor, intenta con otro plan.';
        } else if (backendMessage.includes('Invalid') || backendMessage.includes('invalid')) {
          errorTitle = 'Datos Inválidos';
          errorMessage = 'Los datos proporcionados no son válidos. Por favor, verifica e intenta de nuevo.';
        } else if (backendMessage.includes('Network Error') || backendMessage.includes('timeout')) {
          errorTitle = 'Error de Conexión';
          errorMessage = 'No se pudo conectar con el servidor. Por favor, verifica tu conexión a internet.';
        } else {
          // Usar el mensaje del backend directamente si no coincide con ningún caso conocido
          errorMessage = backendMessage;
        }
      }

      await showErrorAlert(errorTitle, errorMessage);
    } finally {
      setIsCreatingAndUpgrading(false);
    }
  };

  if (isLoadingPlans) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Spinner size="lg" />
      </div>
    );
  }

  if (plansError) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-secondary-900 dark:text-white">Planes y Precios</h1>
          <p className="mt-2 text-secondary-600 dark:text-secondary-400">
            Elige el plan que mejor se adapte a tus necesidades
          </p>
        </div>
        <Alert variant="danger" title="Error al cargar los planes">
          No se pudieron cargar los planes disponibles. Por favor, intenta de nuevo más tarde.
        </Alert>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center">
        <h1 className="text-4xl font-bold text-secondary-900 dark:text-white">
          {product?.name || 'Planes y Precios'}
        </h1>
        <p className="mt-4 text-lg text-secondary-600 dark:text-secondary-400">
          {product?.description || 'Elige el plan que mejor se adapte a tus necesidades de facturación'}
        </p>
      </div>

      {/* Banner de Suscripción Inactiva - Solo si NO es info@hypernovalabs.com y puede gestionar */}
      {!hasActiveSubscription && user?.email !== 'info@hypernovalabs.com' && canManage && (
        <Alert
          variant="warning"
          title="No tienes una suscripción activa"
        >
          <div className="space-y-3">
            <p>
              Actualmente no cuentas con una suscripción activa para esta compañía.
              Puedes activar el plan gratuito o seleccionar uno de los planes premium disponibles a continuación.
            </p>
            <div className="pt-2">
              <Button
                variant="primary"
                size="sm"
                onClick={handleCreateFreeSubscription}
                disabled={createFreeSubscriptionMutation.isPending}
              >
                {createFreeSubscriptionMutation.isPending ? 'Activando...' : 'Activar Plan Gratuito'}
              </Button>
            </div>
          </div>
        </Alert>
      )}

      {/* Plan Actual */}
      {hasActiveSubscription && currentSubscriptionInfo && (
        <Card className="bg-gradient-to-r from-primary-50 to-primary-100 dark:from-primary-900/20 dark:to-primary-800/20 border-primary-200 dark:border-primary-700">
          <CardBody className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="h-12 w-12 rounded-full bg-primary-600 flex items-center justify-center">
                  <SparklesIcon className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-secondary-900 dark:text-white">
                    Plan Actual: {currentSubscriptionInfo.plan_name}
                  </h3>
                  <p className="text-sm text-secondary-600 dark:text-secondary-400">
                    Estado: <Badge variant={currentSubscriptionInfo.status === 'active' ? 'success' : 'warning'}>
                      {currentSubscriptionInfo.status === 'active' ? 'Activo' : currentSubscriptionInfo.status}
                    </Badge>
                  </p>
                  <p className="text-sm text-secondary-600 dark:text-secondary-400 mt-1">
                    Precio: ${currentSubscriptionInfo.price?.toFixed(2) ?? '0.00'} {currentSubscriptionInfo.currency}/año
                  </p>
                </div>
              </div>
            </div>
          </CardBody>
        </Card>
      )}

      {/* Banner: usuario no puede gestionar la suscripción */}
      {hasActiveSubscription && !canManage && (
        <Alert variant="info" title="Solo lectura">
          Solo el administrador que activó el plan puede realizar cambios en la suscripción.
        </Alert>
      )}

      {/* Plans Grid */}
      <div className="grid grid-cols-1 gap-8 lg:grid-cols-2 xl:grid-cols-4">
        {sortedPlans.length === 0 ? (
          <div className="col-span-4 text-center py-12">
            <p className="text-secondary-600 dark:text-secondary-400">
              No hay planes disponibles para este período
            </p>
          </div>
        ) : (
          sortedPlans.map((plan) => {
            const annualPrice = plan.price;
            const monthlyEquivalent = plan.monthly_price;
            const isCurrentPlan = plan.is_current;

            return (
              <Card
                key={plan.id}
                variant="bordered"
                className={cn(
                  // flex-col para que la barra de promo y el cuerpo se REPARTAN la
                  // altura. Con `h-full` en el cuerpo, la barra sumaba su alto por
                  // encima del 100% y el contenido se salía (y overflow-hidden lo
                  // recortaba, dejando el botón mocho).
                  'relative flex flex-col',
                  // overflow-hidden para que la barra de promo recorte en las esquinas
                  isPromoPlan(plan) && 'overflow-hidden',
                  plan.is_popular && !isPromoPlan(plan) && plan.change_type === 'upgrade' && 'border-2 border-primary-600 shadow-xl scale-105',
                  // Promoción: la declara el catálogo en limits.promo. Se distingue
                  // del "Más Popular" con un realce cálido en vez del primario.
                  isPromoPlan(plan) && plan.change_type === 'upgrade' &&
                    'border-2 border-warning-500 shadow-xl ring-2 ring-warning-500/20',
                  plan.change_type === 'downgrade' && 'opacity-60 cursor-not-allowed'
                )}
              >
                {/* `promo` y `bundle` son flags independientes del catálogo:
                     ·  promo + bundle → "Promo: Plan + Certificado" + realce
                     ·  solo promo     → "Promoción" + realce (sin toggle)
                     ·  solo bundle    → sin insignia de promo; el toggle es la oferta
                     ·  ninguno        → tarjeta normal
                    La promoción ocupa el lugar de "Más Popular" en vez de sumarse:
                    dos insignias en la misma esquina compiten entre sí. */}
                {/* La promoción usa una barra de cabecera en vez de una insignia
                    flotante: la insignia se solapaba con el título y con dos líneas
                    de texto quedaba apretada. La barra tolera cualquier largo. */}
                {isPromoPlan(plan) && (
                  <div className="bg-gradient-to-r from-warning-500 to-warning-600 px-4 py-2 flex items-center justify-center gap-1.5">
                    <SparklesIcon className="h-4 w-4 text-white flex-shrink-0" />
                    <span className="text-xs font-bold uppercase tracking-wide text-white text-center">
                      {isBundleEligiblePlan(plan) ? 'Promo · Plan + Certificado' : 'Promoción'}
                    </span>
                  </div>
                )}

                {!isPromoPlan(plan) && plan.is_popular && (
                  <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-10">
                    <Badge variant="primary" className="px-4 py-1">
                      Más Popular
                    </Badge>
                  </div>
                )}

                {/* Con barra de promo, estas insignias bajan para no taparla. */}
                {isCurrentPlan && (
                  <div className={cn('absolute right-4 z-10', isPromoPlan(plan) ? 'top-12' : 'top-4')}>
                    <Badge variant="success">Plan Actual</Badge>
                  </div>
                )}

                {!isCurrentPlan && plan.change_type === 'upgrade' && (
                  <div className={cn('absolute right-4 z-10', isPromoPlan(plan) ? 'top-12' : 'top-4')}>
                    <Badge variant="info">Upgrade</Badge>
                  </div>
                )}

                <CardBody className="p-6 flex flex-col flex-1 min-h-0">
                  {/* Header Section */}
                  <div className="text-center mb-6">
                    <h3 className="text-xl font-bold text-secondary-900 dark:text-white mb-3">
                      {plan.name}
                    </h3>
                    <p className="text-xs text-secondary-600 dark:text-secondary-400 min-h-[4.5rem] leading-relaxed">
                      {plan.description}
                    </p>
                  </div>

                  {/* Price Section */}
                  <div className="text-center mb-6">
                    {isBundleEligiblePlan(plan) && bundlePlanId === plan.id ? (
                      <div className="space-y-2">
                        {/* El bundle cobra los dos productos, así que el precio que se
                            muestra es la suma. El argumento de venta ya no es el ahorro
                            sino la conveniencia: un solo trámite y un solo cargo para
                            quedar operativo. */}
                        <div className="flex items-baseline justify-center gap-1">
                          <span className="text-3xl font-bold text-primary-600">
                            {precioBundle(plan) != null
                              ? `$${precioBundle(plan)!.toFixed(2)}`
                              : '—'}
                          </span>
                          <span className="text-sm text-secondary-600 dark:text-secondary-400">
                            /año
                          </span>
                        </div>
                        {bundlePrice != null && (
                          <p className="text-xs text-secondary-500 dark:text-secondary-400">
                            Certificado {bundlePriceLabel} + {plan.name} $
                            {plan.price.toFixed(2)}
                          </p>
                        )}
                        <div className="inline-flex items-center gap-1 rounded-full bg-primary-100 dark:bg-primary-900/40 px-3 py-1">
                          <span className="text-xs font-semibold text-primary-700 dark:text-primary-300">
                            Todo en un solo pago
                          </span>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="flex items-baseline justify-center gap-1 mb-2">
                          {/* En promoción, el precio de lista tachado antes del de campaña. */}
                          {plan.regular_price != null && plan.regular_price > annualPrice && (
                            <span className="text-lg text-secondary-500 dark:text-secondary-400 line-through">
                              ${plan.regular_price.toFixed(2)}
                            </span>
                          )}
                          <span className="text-3xl font-bold text-primary-600">
                            ${annualPrice.toFixed(2)}
                          </span>
                          <span className="text-sm text-secondary-600 dark:text-secondary-400">
                            /año
                          </span>
                        </div>

                        <div className="min-h-[2rem]">
                          {annualPrice > 0 && (
                            <p className="text-xs text-success-600 dark:text-success-400">
                              Equivalente a ${monthlyEquivalent.toFixed(2)}/mes
                            </p>
                          )}
                        </div>
                      </>
                    )}

                  </div>

                  {/* Limits Section */}
                  <div className="mb-4 p-3 bg-secondary-50 dark:bg-secondary-900 rounded-lg">
                    <h4 className="text-xs font-semibold text-secondary-900 dark:text-white mb-2">
                      Límites del Plan
                    </h4>
                    <div className="space-y-1 text-xs">
                      {plan.limits.facturas_mes !== undefined && (
                        <div className="flex justify-between gap-2">
                          <span className="text-secondary-600 dark:text-secondary-400">
                            Facturas:
                          </span>
                          <span className="font-semibold text-secondary-900 dark:text-white text-right">
                            {plan.limits.facturas_mes === -1 ? 'Ilimitadas' : `${plan.limits.facturas_mes}/mes`}
                          </span>
                        </div>
                      )}
                      {plan.limits.usuarios !== undefined && (
                        <div className="flex justify-between gap-2">
                          <span className="text-secondary-600 dark:text-secondary-400">
                            Usuarios:
                          </span>
                          <span className="font-semibold text-secondary-900 dark:text-white text-right">
                            {plan.limits.usuarios}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Features Section - Flex grow to push button to bottom */}
                  <div className="flex-grow mb-6">
                    <ul className="space-y-2">
                      {plan.features.map((feature, index) => (
                        <li key={index} className="flex items-start gap-2">
                          <CheckCircleIcon className="h-4 w-4 text-success-600 flex-shrink-0 mt-0.5" />
                          <span className="text-xs text-secondary-700 dark:text-secondary-300">
                            {feature}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {isBundleEligiblePlan(plan) && (
                    <div className="mb-6 rounded-lg border border-primary-200 dark:border-primary-700 bg-primary-50/60 dark:bg-primary-900/10 p-3 space-y-2">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="text-xs font-semibold text-secondary-900 dark:text-white">
                            Agregar Certificado de Firma Electrónica
                          </p>
                          <p className="text-xs text-secondary-600 dark:text-secondary-400">
                            Llévate el certificado y este plan juntos, en un solo pago.
                            Si no lo activas, compras el plan solo.
                          </p>
                        </div>
                        <Switch
                          checked={bundlePlanId === plan.id}
                          onChange={(checked) => {
                            setBundlePlanId(checked ? plan.id : null);
                            if (!checked) {
                              sessionStorage.removeItem('certificate_bundle_request');
                              setBundleContext(null);
                              bundleContextRef.current = null;
                            }
                          }}
                          disabled={
                            !canManage ||
                            isCreatingAndUpgrading ||
                            createFreeSubscriptionMutation.isPending ||
                            // Si ya es tu plan, el bundle no aplica: es una oferta
                            // para quien lo está contratando o subiendo de plan.
                            // Un cliente que ya lo tiene compra el certificado por
                            // el portal de certificados.
                            isCurrentPlan ||
                            plan.change_type !== 'upgrade'
                          }
                          size="sm"
                        />
                      </div>
                      {/* Cuando el toggle está apagado la sección de precio muestra
                          el plan normal, así que acá va el argumento para activarlo.
                          Encendido, el precio arriba ya lo explica y repetirlo sobra. */}
                      {bundlePlanId !== plan.id && (
                        <div className="space-y-1 border-t border-primary-200/60 dark:border-primary-700/60 pt-2">
                          <div className="flex items-center justify-between text-xs">
                            <span className="text-secondary-600 dark:text-secondary-400">
                              Certificado
                            </span>
                            <span className="font-semibold text-secondary-900 dark:text-white">
                              {bundlePriceLabel}
                            </span>
                          </div>
                          <div className="flex items-center justify-between text-xs">
                            <span className="text-secondary-600 dark:text-secondary-400">
                              Este plan
                            </span>
                            <span className="font-semibold text-secondary-700 dark:text-secondary-300">
                              ${plan.price.toFixed(2)}
                            </span>
                          </div>
                          {bundlePrice != null && (
                            // "Si lo activas" y no "Total": con el toggle apagado el
                            // botón cobra solo el plan, y un "Total" mayor al del botón
                            // se lee como un cobro sorpresa.
                            <div className="flex items-center justify-between border-t border-secondary-200 dark:border-secondary-700 pt-1.5 text-xs font-semibold text-primary-700 dark:text-primary-300">
                              <span>Si lo activas</span>
                              <span>${precioBundle(plan)!.toFixed(2)}</span>
                            </div>
                          )}
                          <p className="text-[0.7rem] text-secondary-500 dark:text-secondary-400 pt-1">
                            Los dos productos en un solo cargo, con un solo trámite.
                          </p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Action Button - Always at bottom */}
                  <Button
                    variant={isCurrentPlan ? 'outline' : plan.is_popular && plan.change_type === 'upgrade' ? 'primary' : 'outline'}
                    className="w-full"
                    onClick={() => iniciarCompraDePlan(plan)}
                    disabled={
                      !canManage ||
                      isCreatingAndUpgrading ||
                      createFreeSubscriptionMutation.isPending ||
                      (hasActiveSubscription && (
                        isCurrentPlan ||
                        plan.change_type !== 'upgrade' ||
                        upgradeMutation.isPending ||
                        downgradeMutation.isPending ||
                        changePlanWithPaymentMutation.isPending
                      ))
                    }
                    leftIcon={
                      !isCurrentPlan && (hasActiveSubscription || plan.price > 0) && plan.change_type === 'upgrade' ? (
                        <ArrowUpIcon className="w-4 h-4" />
                      ) : undefined
                    }
                  >
                    {isCreatingAndUpgrading || createFreeSubscriptionMutation.isPending
                      ? 'Preparando...'
                      : isBundleEligiblePlan(plan) && bundlePlanId === plan.id
                      ? // El botón anuncia el total del bundle, igual que el precio de
                        // la tarjeta. Usa `precioBundle` y no `getBundlePaymentAmount`
                        // porque ese último todavía devuelve el precio del plan hasta
                        // que el certificado se registra, y acá ya estamos vendiendo
                        // los dos productos.
                        `Continuar con Bundle - $${(precioBundle(plan) ?? plan.price).toFixed(2)}`
                      : !hasActiveSubscription
                      ? plan.price > 0
                        ? `Activar Plan - $${plan.price.toFixed(2)}`
                        : 'Activar Plan Gratuito'
                      : isCurrentPlan
                      ? 'Plan Actual'
                      : upgradeMutation.isPending || downgradeMutation.isPending || changePlanWithPaymentMutation.isPending
                      ? 'Procesando...'
                      : plan.change_type === 'upgrade'
                      ? `Mejorar Plan - $${plan.price_difference.toFixed(2)}`
                      : plan.change_type === 'downgrade'
                      ? 'No disponible'
                      : 'Plan Actual'}
                  </Button>
                </CardBody>
              </Card>
            );
          })
        )}
      </div>

      {/* Product Info */}
      <Card>
        <CardHeader>
          <h2 className="text-xl font-semibold text-secondary-900 dark:text-white">
            Información del Producto
          </h2>
        </CardHeader>
        <CardBody>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="font-medium text-secondary-900 dark:text-white mb-2">
                Todos los planes incluyen:
              </h3>
              <ul className="space-y-2 text-sm text-secondary-600 dark:text-secondary-400">
                <li className="flex items-center gap-2">
                  <CheckCircleIcon className="h-4 w-4 text-success-600" />
                  Facturación electrónica cumpliendo con DGI
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircleIcon className="h-4 w-4 text-success-600" />
                  Gestión de clientes y productos
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircleIcon className="h-4 w-4 text-success-600" />
                  Reportes básicos
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircleIcon className="h-4 w-4 text-success-600" />
                  Actualizaciones automáticas
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircleIcon className="h-4 w-4 text-success-600" />
                  Seguridad y respaldo de datos
                </li>
              </ul>
            </div>
            <div>
              <h3 className="font-medium text-secondary-900 dark:text-white mb-2">
                ¿Tienes preguntas?
              </h3>
              <p className="text-sm text-secondary-600 dark:text-secondary-400 mb-4">
                Nuestro equipo está aquí para ayudarte a elegir el mejor plan para tu negocio.
              </p>
              <div className="space-y-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full"
                  onClick={() => setIsSupportModalOpen(true)}
                >
                  Contactar Soporte
                </Button>
              </div>
            </div>
          </div>
        </CardBody>
      </Card>

      {/* Payment Modal */}
      <PaymentModal
        isOpen={isPaymentModalOpen}
        onClose={() => {
          setIsPaymentModalOpen(false);
          setSelectedPlan(null);
          setNewUserSubscriptionInfo(null);
        }}
        plan={selectedPlan}
        currentSubscriptionId={currentSubscriptionInfo?.id || newUserSubscriptionInfo?.id || null}
        onSubmit={handlePaymentSubmit}
        isProcessing={changePlanWithPaymentMutation.isPending}
      />

      {/* Support Modal */}
      {isSupportModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setIsSupportModalOpen(false)}
          />
          <div className="relative bg-white dark:bg-secondary-800 rounded-xl shadow-xl max-w-md w-full mx-4 overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b border-secondary-200 dark:border-secondary-700">
              <h3 className="text-lg font-semibold text-secondary-900 dark:text-white">
                Contactar Soporte
              </h3>
              <button
                onClick={() => setIsSupportModalOpen(false)}
                className="p-1 rounded-lg hover:bg-secondary-100 dark:hover:bg-secondary-700 transition-colors"
              >
                <XMarkIcon className="h-5 w-5 text-secondary-500" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <p className="text-sm text-secondary-600 dark:text-secondary-400">
                Visita nuestro portal de soporte para obtener ayuda:
              </p>

              {/* Portal de Soporte */}
              <a
                href="https://soporte.facturalibre.io"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 p-4 rounded-lg border border-secondary-200 dark:border-secondary-700 hover:border-primary-300 hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors"
              >
                <div className="p-2 bg-primary-100 dark:bg-primary-900/30 rounded-lg">
                  <GlobeAltIcon className="h-5 w-5 text-primary-600 dark:text-primary-400" />
                </div>
                <div>
                  <p className="font-medium text-secondary-900 dark:text-white">Portal de Soporte</p>
                  <p className="text-sm text-primary-600 dark:text-primary-400">soporte.facturalibre.io</p>
                </div>
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
